import { useEffect } from 'react'

import { Accordion, Link, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { updateUser } from '@/actions/userActions'
import { DQ_REASONS } from '@/reducers/auth/userReducer'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAnalyticsView } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import type { QualificationVerdict } from '@/features/user/signup/your-practice/qualification'

import {
  DQ_MULTI_REASON_SENTENCE,
  DQ_REASON_COPY,
  DQ_REASON_EXPLAINER,
  DQ_REASON_CLAUSE,
  DQ_REASON_TO_PERSISTED,
  DQ_SENTENCE_LEAD,
  DQ_SENTENCE_TAIL,
} from '../lib/qualification'

export type DecidedVerdict = Extract<
  QualificationVerdict,
  { treatment: 'disqualified' | 'waitlist' }
>

const HEADINGS: Record<DecidedVerdict['treatment'], string> = {
  disqualified: 'Heard is not the right fit for your practice yet',
  waitlist: 'You are on our waitlist',
}

const WAITLIST_BODY =
  'A practice your size needs more than we can offer through sign-up today. We will be in touch as soon as we can support you properly.'

const ANALYTICS_VIEWS: Record<DecidedVerdict['treatment'], string> = {
  disqualified: 'compliance report disqualified',
  waitlist: 'compliance report waitlist',
}

const persistedReasons = (verdict: DecidedVerdict): DQ_REASONS[] =>
  verdict.treatment === 'waitlist'
    ? [DQ_REASONS.TOO_MANY_EMPLOYEES]
    : [...new Set(verdict.reasons.map((r) => DQ_REASON_TO_PERSISTED[r]))]

/**
 * Closes the report for practices Heard cannot serve, in place of the next-steps card
 * and in place of it pinning. The report above is unchanged — the findings are useful
 * whether or not we can act on them.
 */
export const DisqualifiedCard: React.FC<{ verdict: DecidedVerdict }> = ({
  verdict,
}) => {
  const dispatch = useAppDispatch()
  const currentUser = useReselector(getCurrentUser)
  const pageView = useAnalyticsView()
  const userId = currentUser?.id
  const reasons = verdict.treatment === 'disqualified' ? verdict.reasons : []
  // Only the reasons the copy table covers; the rest list their bullet and stop there.
  // One reason gets named outright; several are introduced, and the bullets are the list.
  const clause = reasons.length === 1 ? DQ_REASON_CLAUSE[reasons[0]] : undefined
  const explainers = reasons.flatMap((reason) => {
    const copy = DQ_REASON_EXPLAINER[reason]
    return copy ? [{ reason, ...copy }] : []
  })

  useEffect(() => {
    if (!userId) return
    updateUser(userId, {
      disqualifiedReasons: persistedReasons(verdict),
      disqualifiedAt: new Date().toISOString(),
    })(dispatch)
  }, [dispatch, userId, verdict])

  // Keyed on the verdict object, like the write above — the caller memoizes it, so this
  // does not re-fire as the report re-renders.
  useEffect(() => {
    pageView(ANALYTICS_VIEWS[verdict.treatment], {
      reasons: verdict.treatment === 'disqualified' ? verdict.reasons : [],
    })
  }, [pageView, verdict])

  return (
    // Announced because this card can swap in after the intake resolves — a reader who
    // has already heard the next-steps card would otherwise get no signal it became a
    // rejection.
    <div
      role="status"
      aria-live="polite"
      // Full width, below the findings rather than pinned beside them: a decided
      // rejection reads as a closing statement, and pinning would keep it on screen for
      // the whole report. `useReportEndCardSlot` is what puts it there.
      className="mt-8 flex flex-col items-start gap-8 rounded-2xl bg-neutral-25 p-8 md:flex-row"
    >
      <img
        src="https://heard-images.s3.amazonaws.com/assets/seedling.svg"
        alt=""
        className="hidden w-[180px] shrink-0 md:block"
      />
      <div className="flex flex-1 flex-col gap-5">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          Where that leaves us
        </Text>
        <Text variant={TypographyStyles.DISPLAY_MD} color={TextColors.HEADER}>
          {HEADINGS[verdict.treatment]}
        </Text>
        {verdict.treatment === 'waitlist' && (
          <Text variant={TypographyStyles.BODY_MD}>{WAITLIST_BODY}</Text>
        )}
        {clause && (
          <Text variant={TypographyStyles.BODY_MD}>
            {`${DQ_SENTENCE_LEAD} ${clause.verb} `}
            <b>{clause.phrase}</b>
            {`. ${DQ_SENTENCE_TAIL}`}
          </Text>
        )}
        {!clause && reasons.length > 0 && (
          <>
            <Text variant={TypographyStyles.BODY_MD}>
              {DQ_MULTI_REASON_SENTENCE}
            </Text>
            <ul className="ml-4 flex list-disc flex-col gap-1">
              {reasons.map((reason) => (
                <li key={reason}>
                  <Text variant={TypographyStyles.BODY_MD}>
                    {DQ_REASON_COPY[reason]}
                  </Text>
                </li>
              ))}
            </ul>
            <Text variant={TypographyStyles.BODY_MD}>{DQ_SENTENCE_TAIL}</Text>
          </>
        )}
        {explainers.length > 0 && (
          // `single` and collapsible: these are asides to a rejection, not a list to work
          // through, so opening one closes the last.
          <Accordion variant="text" type="single" collapsible>
            {explainers.map(({ reason, question, answer }) => (
              <Accordion.Item key={reason} value={reason}>
                {/* The trigger fills its row and pushes the chevron to the far right,
                  which strands it from the question it belongs to. Releasing the label
                  from `flex-1` lets the two sit together under the trigger's own gap.
                  `flex-initial`, not `flex-none`: the label still must not grow, but
                  it has to be allowed to shrink and wrap, or a long question runs
                  straight out of the card and takes the chevron with it. `min-w-0`
                  is what actually lets it shrink below its text width, and `text-left`
                  keeps the wrapped lines ranged left inside the button. */}
                <Accordion.Trigger className="justify-start text-left [&>div]:min-w-0 [&>div]:flex-initial">
                  {question}
                </Accordion.Trigger>
                <Accordion.Content>
                  <Text variant={TypographyStyles.BODY_SM}>{answer}</Text>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
        {/* Last in the column, after the reasons and their explainers: the
            way back in reads as the close of the rejection, not a preamble. */}
        <Text variant={TypographyStyles.BODY_SM}>
          Think this might be a mistake? Reach out to{' '}
          <Link href="mailto:hello@joinheard.com">hello@joinheard.com</Link>,
          and we’ll review your situation. If your practice changes down the
          line, feel free to come back anytime.
        </Text>
      </div>
    </div>
  )
}
